/**
 * Article registry. Order here = order on /learn index page (newest first).
 *
 * To add an article:
 *  1. Create a new JSX file in this folder using ArticleLayout
 *  2. Import it here and add an entry with slug, title, summary, description, date, component
 *  3. (Optional) Link to it contextually from somewhere in the app
 *  The sitemap, the prerender, and the live smoke test all read this list;
 *  there is nothing else to register.
 */

import CalorieMath from './calorie-math.jsx';
import RecompMyth from './recomp-myth.jsx';
import DeloadWeeks from './deload-weeks.jsx';
import WhyTheScaleLies from './why-the-scale-lies.jsx';
import ProgressiveOverload from './progressive-overload.jsx';
import ProteinBasics from './protein-basics.jsx';
import CardioAndLifting from './cardio-and-lifting.jsx';
import HowTrainForgeUsesAi from './how-trainforge-uses-ai.jsx';
import WhatAiGetsWrong from './what-ai-gets-wrong.jsx';

export const ARTICLES = [
  {
    slug: 'how-trainforge-uses-ai',
    title: 'How TrainForge uses AI in your training',
    summary: 'Not the hype version. A specific walkthrough of where the AI shows up in your program, what it does well, what it doesn\'t try to do, and what\'s coming next.',
    description: 'The specific places TrainForge uses AI — program generation, photo food logging, weekly coaching, adaptive macros, trainer summaries — plus what we deliberately don\'t use it for and what\'s on the roadmap.',
    date: '2026-05-23',
    component: HowTrainForgeUsesAi,
  },
  {
    slug: 'what-ai-gets-wrong',
    title: 'What AI gets wrong about fitness coaching',
    summary: 'AI is useful for some parts of training and oversold for others. The honest list of what AI can\'t replace — and why that\'s not a problem to solve.',
    description: 'The limitations of AI in fitness coaching: form checking from phone video, freeform chat advice, accountability that actually works, knowing when life is the reason for missed sessions. Plus the honest list of what AI is genuinely good at.',
    date: '2026-05-23',
    component: WhatAiGetsWrong,
  },
  {
    slug: 'protein-basics',
    title: 'How much protein do you actually need?',
    summary: 'The internet says 0.4 to 2.0 g/lb. The research says a much narrower range — and the goal you\'re chasing matters more than the total.',
    description: 'Research-backed protein targets for lifters: 0.8–1.0 g/lb for muscle, 1.2 g/lb cutting. Why distribution across 3–5 meals matters, the leucine threshold, and the myths worth busting.',
    date: '2026-05-23',
    component: ProteinBasics,
  },
  {
    slug: 'cardio-and-lifting',
    title: 'Does cardio kill your gains?',
    summary: 'The interference effect is real, but kicks in at much higher volumes than most lifters do. When cardio actually hurts lifting — and when it makes it better.',
    description: 'The interference effect between cardio and lifting is real but applies mainly to elite endurance training. For typical lifters, 2–3 moderate cardio sessions a week help recovery and fat loss without hurting muscle growth.',
    date: '2026-05-23',
    component: CardioAndLifting,
  },
  {
    slug: 'progressive-overload',
    title: 'Progressive overload: how to add weight without stalling',
    summary: 'Adding weight every session works for two weeks. Here\'s how to structure progression so the bar keeps moving for months, including double progression and what to do when you stall.',
    description: 'The four forms of progressive overload, double progression for sustainable strength gains, why compounds progress slower than accessories, and what to actually do when you stall.',
    date: '2026-05-22',
    component: ProgressiveOverload,
  },
  {
    slug: 'calorie-math',
    title: 'How TrainForge calculates your calories',
    summary: 'Plain-English walkthrough of estimated TDEE, measured TDEE, and goal adjustment — and why the number adapts every week once you log data.',
    description: 'How TrainForge calculates your daily calorie target — the activity-multiplier estimate, the 3,500 kcal rule, goal adjustments, and the measured TDEE that takes over once you have data.',
    date: '2026-05-22',
    component: CalorieMath,
  },
  {
    slug: 'recomp-myth',
    title: 'Why recomp is harder than it sounds',
    summary: 'Body recomposition works for fewer people than fitness apps suggest. Here\'s who it works for and what to do if you\'re not in that group.',
    description: 'Body recomposition (gaining muscle and losing fat at the same time) works for beginners, returning lifters, and people with high body fat. For most natural intermediates, alternating cuts and bulks produces 3–5x more change.',
    date: '2026-05-22',
    component: RecompMyth,
  },
  {
    slug: 'deload-weeks',
    title: 'Deload weeks: what they are and why every 4 weeks',
    summary: 'A planned drop in training stress before fatigue becomes a problem. Skipping deloads is one of the most common reasons natural lifters stall.',
    description: 'Deload weeks reduce training volume and intensity by ~50% every fourth week, allowing recovery before fatigue causes a stall. Drop weights 10%, halve your sets, leave 3–4 reps in reserve.',
    date: '2026-05-22',
    component: DeloadWeeks,
  },
  {
    slug: 'why-the-scale-lies',
    title: 'Why the scale isn\'t your progress',
    summary: 'Body weight swings 4–6 lbs in a day from water, glycogen, sodium, hormones, and gut contents. Weekly averages tell the truth; daily numbers are noise.',
    description: 'Body weight can swing 4–6 lbs daily from water, glycogen, sodium, hormonal retention, and inflammation. Fat changes at most 0.5 lb/day. Use weekly averages, not daily readings.',
    date: '2026-05-22',
    component: WhyTheScaleLies,
  },
];

export function findArticle(slug) {
  return ARTICLES.find((a) => a.slug === slug) || null;
}

/**
 * Pick up to N related articles, excluding the current one. Right now this
 * is simple "all the others, newest first" — when the list grows past ~8
 * articles, switch to tag-based matching.
 */
export function relatedArticles(currentSlug, n = 3) {
  return ARTICLES.filter((a) => a.slug !== currentSlug).slice(0, n);
}
